import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, ToggleButton, ToggleButtonGroup, Grid, Skeleton, alpha, useTheme,
  Stack, Paper,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import { CheckCircle, Cancel, AccountBalanceWallet, CreditCard, Payments } from '@mui/icons-material';
import { api } from '../../services/core/api';
import { selectTenantSlug } from '../../store/slices/tenantSlice';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';
import DashboardSection from './DashboardSection';
import DashboardChartFrame from './DashboardChartFrame';
import { useDashboardChartLayout } from './useDashboardChartLayout';
import { DASHBOARD_SERVICE_NAV, DASHBOARD_NAV } from '../../config/dashboardNavigation';
import { DASHBOARD_POLL_MS } from '../../hooks/useDashboardLiveRefresh';

type Period = 'day' | 'week' | 'month' | 'year';

const PAYMENT_COLORS = { cash: '#16a34a', wallet: '#2563eb', card: '#7c3aed' };
const SERVICE_COLORS: Record<string, string> = {
  transport: '#2563eb', porter: '#0f766e', on_demand: '#0891b2',
};

function PeriodTooltip({ active, payload, label, formatAmount }: {
  active?: boolean;
  payload?: Array<{ payload?: { completed?: number; cancelled?: number; revenue?: number } }>;
  label?: string;
  formatAmount: (n: number) => string;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <Paper sx={{ p: 1.25, fontSize: 12, boxShadow: 3, maxWidth: 260 }}>
      <Typography variant="subtitle2" fontWeight={700} mb={0.5}>{label}</Typography>
      <Typography variant="body2">Completed: <b>{row.completed ?? 0}</b></Typography>
      <Typography variant="body2">Cancelled: <b>{row.cancelled ?? 0}</b></Typography>
      <Typography variant="body2">Revenue: <b>{formatAmount(row.revenue ?? 0)}</b></Typography>
    </Paper>
  );
}

function DashboardPeriodReports() {
  const theme = useTheme();
  const layout = useDashboardChartLayout();
  const navigate = useNavigate();
  const tenantSlug = useSelector(selectTenantSlug);
  const { formatAmount } = useTenantCurrency();
  const [period, setPeriod] = useState<Period>('day');
  const [customFrom, setCustomFrom] = useState<Dayjs | null>(null);
  const [customTo, setCustomTo] = useState<Dayjs | null>(null);

  const queryParams = useMemo(() => {
    const p: Record<string, string> = { period };
    if (customFrom) p.from = customFrom.startOf('day').toISOString();
    if (customTo) p.to = customTo.endOf('day').toISOString();
    return p;
  }, [period, customFrom, customTo]);

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-reports', tenantSlug, period, customFrom?.toISOString(), customTo?.toISOString()],
    queryFn: async () => (await api.get('/analytics/reports', { params: queryParams })).data.data,
    refetchInterval: DASHBOARD_POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const paymentTxPath = (method: string) => {
    const params = new URLSearchParams({ method });
    if (customFrom) params.set('from', customFrom.startOf('day').toISOString());
    if (customTo) params.set('to', customTo.endOf('day').toISOString());
    return `${DASHBOARD_NAV.paymentTransactions}?${params.toString()}`;
  };

  const totals = data?.totals || { completed: 0, cancelled: 0, revenue: 0 };
  const statusTrend = data?.statusTrend || [];
  const serviceTotals = useMemo(() => {
    const rows = (data?.serviceTotals || []) as Array<{
      key: string;
      label: string;
      completed: number;
      cancelled: number;
      revenue: number;
    }>;
    return rows.filter((s) => s.key !== 'food' && s.key !== 'grocery');
  }, [data?.serviceTotals]);
  const paymentBreakdown = data?.paymentBreakdown || {
    cash: { count: 0, amount: 0 },
    wallet: { count: 0, amount: 0 },
    card: { count: 0, amount: 0 },
  };

  const pieData = [
    { name: 'Cash', value: paymentBreakdown.cash?.amount || 0, key: 'cash' },
    { name: 'Wallet', value: paymentBreakdown.wallet?.amount || 0, key: 'wallet' },
    { name: 'Card / Online', value: paymentBreakdown.card?.amount || 0, key: 'card' },
  ].filter((d) => d.value > 0);

  const statCards = [
    { label: 'Completed', value: totals.completed, icon: CheckCircle, color: theme.palette.success.main },
    { label: 'Cancelled', value: totals.cancelled, icon: Cancel, color: theme.palette.error.main },
    { label: 'Revenue', value: formatAmount(totals.revenue || 0), icon: Payments, color: theme.palette.primary.main },
  ];

  const paymentCards = [
    { key: 'cash', label: 'Cash', icon: Payments, ...paymentBreakdown.cash },
    { key: 'wallet', label: 'Wallet', icon: AccountBalanceWallet, ...paymentBreakdown.wallet },
    { key: 'card', label: 'Card / Online', icon: CreditCard, ...paymentBreakdown.card },
  ];

  const periodLabel = period === 'day' ? 'Daily' : period === 'week' ? 'Weekly' : period === 'month' ? 'Monthly' : 'Yearly';
  const tickInterval = useMemo(() => {
    const len = statusTrend.length;
    if (len <= 10) return 0;
    return Math.ceil(len / 10) - 1;
  }, [statusTrend]);

  const formatDateTick = (v: string) => {
    if (v.includes('W')) return v;
    const parts = v.split('-');
    if (parts.length === 3) return `${parts[1]}/${parts[2]}`;
    if (parts.length === 2) return `${parts[1]}/${parts[0].slice(2)}`;
    return v;
  };

  return (
    <DashboardSection
      title="Period intelligence"
      subtitle="Completed vs cancelled trends and payment channel breakdown"
      accent="#0d9488"
      noPadding
    >
      <Box px={{ xs: 2, md: 2.5 }} pt={0} pb={2.5} sx={{ width: '100%', minWidth: 0 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={2} alignItems={{ sm: 'center' }} justifyContent="space-between" flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={period}
            onChange={(_, v) => v && setPeriod(v)}
            sx={{ '& .MuiToggleButton-root': { flex: { xs: 1, sm: 'initial' } } }}
          >
            <ToggleButton value="day">Daily</ToggleButton>
            <ToggleButton value="week">Weekly</ToggleButton>
            <ToggleButton value="month">Monthly</ToggleButton>
            <ToggleButton value="year">Yearly</ToggleButton>
          </ToggleButtonGroup>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack direction="row" spacing={1} alignItems="center">
              <DatePicker
                label="From"
                value={customFrom}
                onChange={(v) => setCustomFrom(v)}
                slotProps={{ textField: { size: 'small', sx: { maxWidth: 150 } } }}
                maxDate={customTo || undefined}
              />
              <DatePicker
                label="To"
                value={customTo}
                onChange={(v) => setCustomTo(v)}
                slotProps={{ textField: { size: 'small', sx: { maxWidth: 150 } } }}
                minDate={customFrom || undefined}
              />
            </Stack>
          </LocalizationProvider>
        </Stack>

        <Grid container spacing={1.5} mb={3}>
          {statCards.map((s) => {
            const Icon = s.icon;
            return (
              <Grid item xs={12} sm={4} key={s.label}>
                {isLoading ? (
                  <Skeleton variant="rounded" height={72} />
                ) : (
                  <Box sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: alpha(s.color, 0.25), bgcolor: alpha(s.color, 0.06), height: '100%' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Icon sx={{ fontSize: 20, color: s.color, flexShrink: 0 }} />
                      <Box minWidth={0}>
                        <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                        <Typography variant="h6" fontWeight={800} lineHeight={1.2} noWrap>{s.value}</Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Grid>
            );
          })}
        </Grid>

        {serviceTotals.length > 0 && (
          <>
            <Typography variant="subtitle2" fontWeight={800} mb={1}>
              {periodLabel} breakdown by service
            </Typography>
            <Grid container spacing={1} mb={3}>
              {serviceTotals.map((svc) => {
                const color = SERVICE_COLORS[svc.key] || '#64748b';
                const path = DASHBOARD_SERVICE_NAV[svc.key];
                return (
                  <Grid item xs={6} sm={4} md={4} key={svc.key}>
                    <Box
                      onClick={() => path && navigate(path)}
                      sx={{
                        p: 1.25, borderRadius: 2, border: 1,
                        borderColor: alpha(color, 0.3), bgcolor: alpha(color, 0.06),
                        cursor: path ? 'pointer' : 'default', height: '100%',
                        '&:hover': path ? { bgcolor: alpha(color, 0.12) } : {},
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5}>
                        <Box sx={{ width: 8, height: 8, borderRadius: 999, bgcolor: color }} />
                        <Typography variant="caption" fontWeight={700} color="text.secondary">{svc.label}</Typography>
                      </Stack>
                      <Typography variant="body2">
                        <b style={{ color: theme.palette.success.main }}>{svc.completed}</b> completed ·{' '}
                        <b style={{ color: theme.palette.error.main }}>{svc.cancelled}</b> cancelled
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Revenue: {formatAmount(svc.revenue || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}

        <Typography variant="subtitle2" fontWeight={800} mb={1}>
          Completed vs cancelled · Revenue
        </Typography>
        <DashboardChartFrame loading={isLoading} size="md">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={statusTrend}
              margin={{ ...layout.margin, bottom: 35 }}
              onClick={(state) => {
                const dateVal = (state?.activePayload?.[0]?.payload as { date?: string } | undefined)?.date;
                if (dateVal) navigate(`/reports?date=${dateVal}`);
              }}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.8)} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#475569' }}
                angle={-30}
                textAnchor="end"
                height={50}
                interval={tickInterval}
                tickFormatter={formatDateTick}
              />
              <YAxis tick={layout.axis.tick} width={layout.axis.y.width} allowDecimals={false} />
              <Tooltip content={<PeriodTooltip formatAmount={formatAmount} />} />
              <Legend {...layout.legend} />
              <Bar dataKey="completed" name="Completed" fill={theme.palette.success.main} radius={[4, 4, 0, 0]} />
              <Bar dataKey="cancelled" name="Cancelled" fill={theme.palette.error.main} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </DashboardChartFrame>

        <Typography variant="subtitle2" fontWeight={800} mb={1} mt={3}>
          Payment breakdown (cash / wallet / card) · Click a method to view transactions
        </Typography>
        <DashboardChartFrame loading={isLoading} size="sm">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 8, bottom: layout.isMobile ? 48 : 32 }}>
              <Pie
                data={pieData.length ? pieData : [{ name: 'No data', value: 1, key: 'cash' }]}
                dataKey="value"
                nameKey="name"
                innerRadius={layout.pie.innerRadius}
                outerRadius={layout.pie.outerRadius}
                cx="50%"
                cy={layout.pie.cy}
                style={{ cursor: pieData.length ? 'pointer' : 'default' }}
                onClick={(_, index) => {
                  const entry = pieData[index];
                  if (entry?.key) navigate(paymentTxPath(entry.key));
                }}
              >
                {(pieData.length ? pieData : [{ key: 'cash' }]).map((entry) => (
                  <Cell key={entry.key} fill={PAYMENT_COLORS[entry.key as keyof typeof PAYMENT_COLORS] || '#94a3b8'} />
                ))}
              </Pie>
              <Tooltip {...layout.tooltip} formatter={(v: number) => formatAmount(v)} />
              <Legend
                {...layout.legend}
                onClick={(entry: { value?: string }) => {
                  const key = pieData.find((d) => d.name === entry.value)?.key;
                  if (key) navigate(paymentTxPath(key));
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </DashboardChartFrame>

        <Grid container spacing={1.5} mt={2}>
          {paymentCards.map((p) => {
            const Icon = p.icon;
            const color = PAYMENT_COLORS[p.key as keyof typeof PAYMENT_COLORS];
            return (
              <Grid item xs={12} sm={4} key={p.key}>
                <Box
                  onClick={() => navigate(paymentTxPath(p.key))}
                  sx={{
                    textAlign: 'center', p: 1.5, borderRadius: 2, border: 1,
                    borderColor: alpha(color, 0.2), bgcolor: alpha(color, 0.06), height: '100%',
                    cursor: 'pointer', '&:hover': { bgcolor: alpha(color, 0.12) },
                  }}
                >
                  <Icon sx={{ fontSize: 20, color, mb: 0.5 }} />
                  <Typography variant="caption" display="block" color="text.secondary">{p.label}</Typography>
                  <Typography variant="h6" fontWeight={800}>{formatAmount(p.amount || 0)}</Typography>
                  <Typography variant="caption" color="text.secondary">{p.count || 0} txns</Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </DashboardSection>
  );
}

export default DashboardPeriodReports;
